import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { resetApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "/logo.svg";

export default function ResetPassword() {
    const { token } = useParams();
    const [status, setStatus] = useState('validating');
    const [userInfo, setUserInfo] = useState(null);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        resetApi.validateToken(token)
            .then(({ data }) => { setUserInfo(data); setStatus('valid'); })
            .catch((err) => { setStatus('invalid'); });
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        if (password !== confirm) { toast.error('Passwords do not match'); return; }

        setSubmitting(true);
        try {
            await resetApi.resetPassword(token, password);
            setStatus('success');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to reset password');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-6">
                    <img src={LOGO_URL} alt="Food Safe Hub" className="h-14 mx-auto mb-3" />
                    <h1 className="text-2xl font-bold text-slate-100">Reset Password</h1>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6" data-testid="reset-password-page">
                    {status === 'validating' && (
                        <div className="flex flex-col items-center py-8 text-slate-400">
                            <Loader2 className="h-8 w-8 animate-spin mb-3" />
                            <p className="text-sm">Validating reset link...</p>
                        </div>
                    )}

                    {status === 'invalid' && (
                        <div className="flex flex-col items-center py-8">
                            <XCircle className="h-12 w-12 text-rose-400 mb-3" />
                            <h2 className="text-lg font-semibold text-slate-200 mb-1">Invalid or Expired Link</h2>
                            <p className="text-sm text-slate-400 text-center mb-4">This reset link is no longer valid. Please ask your manager to send a new one.</p>
                            <Link to="/login">
                                <Button className="brand-btn font-medium" data-testid="back-to-login">Back to Sign In</Button>
                            </Link>
                        </div>
                    )}

                    {status === 'valid' && (
                        <>
                            <div className="flex items-center gap-2 mb-4">
                                <Lock className="h-5 w-5 brand-icon" />
                                <h2 className="text-lg font-semibold text-slate-200">Set New Password</h2>
                            </div>
                            {userInfo && (
                                <p className="text-sm text-slate-400 mb-4">
                                    Setting password for <span className="text-slate-200 font-medium">{userInfo.name}</span> ({userInfo.email})
                                </p>
                            )}
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <Label className="text-slate-300">New Password</Label>
                                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Min 6 characters"
                                        data-testid="reset-new-password" required />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Confirm Password</Label>
                                    <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Confirm password"
                                        data-testid="reset-confirm-password" required />
                                </div>
                                <Button type="submit" disabled={submitting}
                                    className="w-full brand-btn font-semibold" data-testid="reset-submit-button">
                                    {submitting ? 'Resetting...' : 'Reset Password'}
                                </Button>
                            </form>
                        </>
                    )}

                    {status === 'success' && (
                        <div className="flex flex-col items-center py-8">
                            <CheckCircle className="h-12 w-12 text-emerald-400 mb-3" />
                            <h2 className="text-lg font-semibold text-slate-200 mb-1">Password Reset!</h2>
                            <p className="text-sm text-slate-400 text-center mb-4">Your password has been updated. You can now sign in with your new password.</p>
                            <Link to="/login">
                                <Button className="brand-btn font-medium" data-testid="go-to-login-button">Go to Sign In</Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
